import React, { useState } from 'react';
import { View, type ViewProps, type ViewStyle, type LayoutChangeEvent } from 'react-native';
import { useBreakpoint } from '../lib/hooks';
import { useTheme } from './theme';

type FlexProps = ViewProps & {
  gap?: number;
  align?: ViewStyle['alignItems'];
  justify?: ViewStyle['justifyContent'];
  wrap?: boolean;
  flex?: number;
  padding?: number;
};

/** Horizontal flex row (wraps by default). */
export function Row({ gap = 8, align = 'center', justify, wrap = true, flex, padding, style, ...rest }: FlexProps) {
  return <View {...rest} style={[{ flexDirection: 'row', gap, alignItems: align, justifyContent: justify, flexWrap: wrap ? 'wrap' : 'nowrap', flex, padding }, style]} />;
}

/** Vertical stack. */
export function Stack({ gap = 16, align, justify, flex, padding, style, ...rest }: FlexProps) {
  return <View {...rest} style={[{ flexDirection: 'column', gap, alignItems: align, justifyContent: justify, flex, padding }, style]} />;
}

export function Spacer({ size = 8, flex }: { size?: number; flex?: boolean }) {
  return <View style={flex ? { flex: 1 } : { width: size, height: size }} />;
}

export function Divider({ vertical, spacing = 0 }: { vertical?: boolean; spacing?: number }) {
  const { colors } = useTheme();
  return <View style={vertical ? { width: 1, alignSelf: 'stretch', backgroundColor: colors.border, marginHorizontal: spacing } : { height: 1, backgroundColor: colors.border, marginVertical: spacing }} />;
}

/**
 * Responsive grid: as many columns of at least `min` px as fit (1 column on phones).
 * `<Grid min={200}>{cards}</Grid>`
 */
export function Grid({ min = 200, gap = 12, maxCols, children, style }: { min?: number; gap?: number; maxCols?: number; children: React.ReactNode; style?: ViewStyle }) {
  const [w, setW] = useState(0);
  const onLayout = (e: LayoutChangeEvent) => setW(e.nativeEvent.layout.width);
  let cols = w ? Math.max(1, Math.floor((w + gap) / (min + gap))) : 1;
  if (maxCols) cols = Math.min(cols, maxCols);
  // Round down so sub-pixel widths never push the last column onto a new row.
  const itemW = w ? Math.floor((w - gap * (cols - 1)) / cols) - 1 : undefined;
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <View onLayout={onLayout} style={[{ flexDirection: 'row', flexWrap: 'wrap', gap }, style]}>
      {items.map((child, i) => (
        <View key={i} style={itemW ? { width: itemW } : { width: '100%' }}>
          {child}
        </View>
      ))}
    </View>
  );
}

/**
 * Two-column layout on tablets/desktop (>= 900px), stacked on phones.
 * `ratio` is the left:right flex ratio.
 */
export function Columns({ children, ratio = [3, 2], gap = 16 }: { children: [React.ReactNode, React.ReactNode]; ratio?: [number, number]; gap?: number }) {
  const { isTablet } = useBreakpoint();
  if (!isTablet) return <Stack gap={gap}>{children}</Stack>;
  return (
    <View style={{ flexDirection: 'row', gap, alignItems: 'flex-start' }}>
      <View style={{ flex: ratio[0], minWidth: 0 }}>{children[0]}</View>
      <View style={{ flex: ratio[1], minWidth: 0 }}>{children[1]}</View>
    </View>
  );
}
